import c from 'compact-encoding'
import b4a from 'b4a'
import { PROTOCOL_VERSION } from './constants.js'

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

export function encodeWith (enc, value) {
  const state = c.state()
  enc.preencode(state, value)
  state.buffer = b4a.allocUnsafe(state.end)
  enc.encode(state, value)
  return state.buffer
}

export function decodeWith (enc, buffer) {
  return enc.decode({ start: 0, end: buffer.byteLength, buffer })
}

/** fixed32 that is allowed to be null, encoded as a one-byte presence flag. */
const optionalFixed32 = {
  preencode (state, v) {
    c.uint.preencode(state, v ? 1 : 0)
    if (v) c.fixed32.preencode(state, v)
  },
  encode (state, v) {
    c.uint.encode(state, v ? 1 : 0)
    if (v) c.fixed32.encode(state, v)
  },
  decode (state) {
    return c.uint.decode(state) ? c.fixed32.decode(state) : null
  }
}

/** compact-encoding's `buffer` throws on null, so nullable fields need a flag. */
const optionalBuffer = {
  preencode (state, v) {
    c.uint.preencode(state, v ? 1 : 0)
    if (v) c.buffer.preencode(state, v)
  },
  encode (state, v) {
    c.uint.encode(state, v ? 1 : 0)
    if (v) c.buffer.encode(state, v)
  },
  decode (state) {
    return c.uint.decode(state) ? c.buffer.decode(state) : null
  }
}

/** Hoisted array codecs — c.array() allocates a new codec on every call. */
const stringArray = c.array(c.string)
const uintArray = c.array(c.uint)
const fixed32Array = c.array(c.fixed32)

export { optionalFixed32, optionalBuffer, stringArray, uintArray, fixed32Array }

/* ------------------------------------------------------------------ *
 * Attachment
 * ------------------------------------------------------------------ */

export const attachmentEncoding = {
  preencode (state, a) {
    c.buffer.preencode(state, a.blobId)
    optionalFixed32.preencode(state, a.core || null)
    c.string.preencode(state, a.name || '')
    c.string.preencode(state, a.mime || 'application/octet-stream')
    c.uint.preencode(state, a.size || 0)
    c.uint.preencode(state, a.width || 0)
    c.uint.preencode(state, a.height || 0)
    c.uint.preencode(state, a.duration || 0)
    optionalBuffer.preencode(state, a.thumb || null)
    optionalFixed32.preencode(state, a.key || null)
    c.string.preencode(state, a.blurhash || '')
    optionalBuffer.preencode(state, a.waveform || null)
  },
  encode (state, a) {
    c.buffer.encode(state, a.blobId)
    optionalFixed32.encode(state, a.core || null)
    c.string.encode(state, a.name || '')
    c.string.encode(state, a.mime || 'application/octet-stream')
    c.uint.encode(state, a.size || 0)
    c.uint.encode(state, a.width || 0)
    c.uint.encode(state, a.height || 0)
    c.uint.encode(state, a.duration || 0)
    optionalBuffer.encode(state, a.thumb || null)
    optionalFixed32.encode(state, a.key || null)
    c.string.encode(state, a.blurhash || '')
    optionalBuffer.encode(state, a.waveform || null)
  },
  decode (state) {
    return {
      blobId: c.buffer.decode(state),
      core: optionalFixed32.decode(state),
      name: c.string.decode(state),
      mime: c.string.decode(state),
      size: c.uint.decode(state),
      width: c.uint.decode(state),
      height: c.uint.decode(state),
      duration: c.uint.decode(state),
      thumb: optionalBuffer.decode(state),
      key: optionalFixed32.decode(state),
      blurhash: c.string.decode(state),
      waveform: optionalBuffer.decode(state)
    }
  }
}

const attachmentArray = c.array(attachmentEncoding)

/* ------------------------------------------------------------------ *
 * Rich-text entity
 * ------------------------------------------------------------------ */

export const entityEncoding = {
  preencode (state, e) {
    c.uint.preencode(state, e.type)
    c.uint.preencode(state, e.offset)
    c.uint.preencode(state, e.length)
    c.string.preencode(state, e.value || '')
  },
  encode (state, e) {
    c.uint.encode(state, e.type)
    c.uint.encode(state, e.offset)
    c.uint.encode(state, e.length)
    c.string.encode(state, e.value || '')
  },
  decode (state) {
    return {
      type: c.uint.decode(state),
      offset: c.uint.decode(state),
      length: c.uint.decode(state),
      value: c.string.decode(state)
    }
  }
}

const entityArray = c.array(entityEncoding)

/* ------------------------------------------------------------------ *
 * Poll
 * ------------------------------------------------------------------ */

export const pollEncoding = {
  preencode (state, p) {
    c.string.preencode(state, p.question)
    stringArray.preencode(state, p.options)
    c.uint.preencode(state, p.flags || 0) // 1 = multiple, 2 = anonymous, 4 = quiz
    c.uint.preencode(state, p.correct === undefined ? 255 : p.correct)
    c.uint.preencode(state, p.closesAt || 0)
  },
  encode (state, p) {
    c.string.encode(state, p.question)
    stringArray.encode(state, p.options)
    c.uint.encode(state, p.flags || 0)
    c.uint.encode(state, p.correct === undefined ? 255 : p.correct)
    c.uint.encode(state, p.closesAt || 0)
  },
  decode (state) {
    return {
      question: c.string.decode(state),
      options: stringArray.decode(state),
      flags: c.uint.decode(state),
      correct: c.uint.decode(state),
      closesAt: c.uint.decode(state)
    }
  }
}

/* ------------------------------------------------------------------ *
 * Message body
 * ------------------------------------------------------------------ */

export const messageEncoding = {
  preencode (state, m) {
    c.uint.preencode(state, m.kind)
    c.string.preencode(state, m.text || '')
    optionalFixed32.preencode(state, m.replyTo || null)
    optionalFixed32.preencode(state, m.threadRoot || null)
    optionalFixed32.preencode(state, m.forwardFrom || null)
    c.string.preencode(state, m.forwardName || '')
    attachmentArray.preencode(state, m.attachments || [])
    entityArray.preencode(state, m.entities || [])
    c.uint.preencode(state, m.ttl || 0)
    c.string.preencode(state, m.extra || '')
  },
  encode (state, m) {
    c.uint.encode(state, m.kind)
    c.string.encode(state, m.text || '')
    optionalFixed32.encode(state, m.replyTo || null)
    optionalFixed32.encode(state, m.threadRoot || null)
    optionalFixed32.encode(state, m.forwardFrom || null)
    c.string.encode(state, m.forwardName || '')
    attachmentArray.encode(state, m.attachments || [])
    entityArray.encode(state, m.entities || [])
    c.uint.encode(state, m.ttl || 0)
    c.string.encode(state, m.extra || '')
  },
  decode (state) {
    return {
      kind: c.uint.decode(state),
      text: c.string.decode(state),
      replyTo: optionalFixed32.decode(state),
      threadRoot: optionalFixed32.decode(state),
      forwardFrom: optionalFixed32.decode(state),
      forwardName: c.string.decode(state),
      attachments: attachmentArray.decode(state),
      entities: entityArray.decode(state),
      ttl: c.uint.decode(state),
      extra: c.string.decode(state)
    }
  }
}

/* ------------------------------------------------------------------ *
 * Other payloads
 * ------------------------------------------------------------------ */

export const editEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.target)
    c.string.preencode(state, m.text)
    entityArray.preencode(state, m.entities || [])
  },
  encode (state, m) {
    c.fixed32.encode(state, m.target)
    c.string.encode(state, m.text)
    entityArray.encode(state, m.entities || [])
  },
  decode (state) {
    return {
      target: c.fixed32.decode(state),
      text: c.string.decode(state),
      entities: entityArray.decode(state)
    }
  }
}

export const deleteEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.target)
    c.uint.preencode(state, m.scope)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.target)
    c.uint.encode(state, m.scope)
  },
  decode (state) {
    return { target: c.fixed32.decode(state), scope: c.uint.decode(state) }
  }
}

export const reactionEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.target)
    c.string.preencode(state, m.emoji)
    c.uint.preencode(state, m.remove ? 1 : 0)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.target)
    c.string.encode(state, m.emoji)
    c.uint.encode(state, m.remove ? 1 : 0)
  },
  decode (state) {
    return {
      target: c.fixed32.decode(state),
      emoji: c.string.decode(state),
      remove: c.uint.decode(state) === 1
    }
  }
}

export const memberEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.account)
    c.string.preencode(state, m.name || '')
    c.uint.preencode(state, m.role)
    optionalBuffer.preencode(state, m.avatar || null)
    c.string.preencode(state, m.bio || '')
  },
  encode (state, m) {
    c.fixed32.encode(state, m.account)
    c.string.encode(state, m.name || '')
    c.uint.encode(state, m.role)
    optionalBuffer.encode(state, m.avatar || null)
    c.string.encode(state, m.bio || '')
  },
  decode (state) {
    return {
      account: c.fixed32.decode(state),
      name: c.string.decode(state),
      role: c.uint.decode(state),
      avatar: optionalBuffer.decode(state),
      bio: c.string.decode(state)
    }
  }
}

export const roleEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.account)
    c.uint.preencode(state, m.role)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.account)
    c.uint.encode(state, m.role)
  },
  decode (state) {
    return { account: c.fixed32.decode(state), role: c.uint.decode(state) }
  }
}

export const roomMetaEncoding = {
  preencode (state, m) {
    c.string.preencode(state, m.name || '')
    c.string.preencode(state, m.description || '')
    optionalBuffer.preencode(state, m.avatar || null)
    c.uint.preencode(state, m.type)
    c.string.preencode(state, m.settings || '{}')
  },
  encode (state, m) {
    c.string.encode(state, m.name || '')
    c.string.encode(state, m.description || '')
    optionalBuffer.encode(state, m.avatar || null)
    c.uint.encode(state, m.type)
    c.string.encode(state, m.settings || '{}')
  },
  decode (state) {
    return {
      name: c.string.decode(state),
      description: c.string.decode(state),
      avatar: optionalBuffer.decode(state),
      type: c.uint.decode(state),
      settings: c.string.decode(state)
    }
  }
}

export const readEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.upTo)
    c.uint.preencode(state, m.at)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.upTo)
    c.uint.encode(state, m.at)
  },
  decode (state) {
    return { upTo: c.fixed32.decode(state), at: c.uint.decode(state) }
  }
}

export const pinEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.target)
    c.uint.preencode(state, m.remove ? 1 : 0)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.target)
    c.uint.encode(state, m.remove ? 1 : 0)
  },
  decode (state) {
    return { target: c.fixed32.decode(state), remove: c.uint.decode(state) === 1 }
  }
}

export const pollVoteEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.poll)
    uintArray.preencode(state, m.options)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.poll)
    uintArray.encode(state, m.options)
  },
  decode (state) {
    return { poll: c.fixed32.decode(state), options: uintArray.decode(state) }
  }
}

export const callLogEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.callId)
    c.uint.preencode(state, m.state)
    c.uint.preencode(state, m.video ? 1 : 0)
    c.uint.preencode(state, m.startedAt)
    c.uint.preencode(state, m.duration)
    fixed32Array.preencode(state, m.participants || [])
  },
  encode (state, m) {
    c.fixed32.encode(state, m.callId)
    c.uint.encode(state, m.state)
    c.uint.encode(state, m.video ? 1 : 0)
    c.uint.encode(state, m.startedAt)
    c.uint.encode(state, m.duration)
    fixed32Array.encode(state, m.participants || [])
  },
  decode (state) {
    return {
      callId: c.fixed32.decode(state),
      state: c.uint.decode(state),
      video: c.uint.decode(state) === 1,
      startedAt: c.uint.decode(state),
      duration: c.uint.decode(state),
      participants: fixed32Array.decode(state)
    }
  }
}

export const writerEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.key)
    c.fixed32.preencode(state, m.account)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.key)
    c.fixed32.encode(state, m.account)
  },
  decode (state) {
    return { key: c.fixed32.decode(state), account: c.fixed32.decode(state) }
  }
}

/* ------------------------------------------------------------------ *
 * Signed operation envelope — the only thing written to a core
 * ------------------------------------------------------------------ */

export const opEncoding = {
  preencode (state, o) {
    c.uint.preencode(state, o.version ?? PROTOCOL_VERSION)
    c.uint.preencode(state, o.type)
    c.fixed32.preencode(state, o.id)
    c.fixed32.preencode(state, o.author)
    c.uint.preencode(state, o.timestamp)
    c.buffer.preencode(state, o.payload)
    c.fixed64.preencode(state, o.signature)
  },
  encode (state, o) {
    c.uint.encode(state, o.version ?? PROTOCOL_VERSION)
    c.uint.encode(state, o.type)
    c.fixed32.encode(state, o.id)
    c.fixed32.encode(state, o.author)
    c.uint.encode(state, o.timestamp)
    c.buffer.encode(state, o.payload)
    c.fixed64.encode(state, o.signature)
  },
  decode (state) {
    return {
      version: c.uint.decode(state),
      type: c.uint.decode(state),
      id: c.fixed32.decode(state),
      author: c.fixed32.decode(state),
      timestamp: c.uint.decode(state),
      payload: c.buffer.decode(state),
      signature: c.fixed64.decode(state)
    }
  }
}

/** Payload codec table, keyed by OP type. */
export const PAYLOAD = {
  0: messageEncoding,
  1: editEncoding,
  2: deleteEncoding,
  3: reactionEncoding,
  4: memberEncoding,
  5: roleEncoding,
  6: roleEncoding,
  7: roomMetaEncoding,
  8: readEncoding,
  9: pinEncoding,
  10: pollVoteEncoding,
  11: callLogEncoding,
  12: writerEncoding,
  13: writerEncoding
}
